'use client';

import * as React from 'react';
import { Check, Copy, Link2 } from 'lucide-react';
import { Button, type ButtonProps } from '@/shared/ui/button';
import { cn } from '@/lib/utils';
import { notify } from '@/shared/lib/notify';

/**
 * One-click copy-a-link button with its own transient "Copied" feedback. Used
 * for every share-link surface — creation success, the in-call host share, the
 * dashboard recents — so copying reads identically everywhere. A successful copy
 * is a quiet inline swap (icon → check); a copy *failure* is a real failure and
 * routes through `notify.error` with a user-safe message, never raw error text.
 */
export function CopyLinkButton({
  url,
  idleLabel,
  copiedLabel = 'Copied',
  successMessage,
  variant = 'default',
  size,
  icon = <Copy className="h-4 w-4" aria-hidden="true" />,
  className,
}: {
  url: string;
  idleLabel: string;
  copiedLabel?: string;
  successMessage?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  icon?: React.ReactNode;
  className?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<number | null>(null);
  React.useEffect(() => () => void (timer.current && window.clearTimeout(timer.current)), []);

  const onCopy = React.useCallback(async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(url);
      setCopied(true);
      if (successMessage) notify.success(successMessage);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    } catch (err) {
      notify.error(err, "Couldn't copy — select and copy the link manually.");
    }
  }, [url, successMessage]);

  // Copied = a soft-green SUCCESS confirmation (global tokens), not the navy
  // "secondary" fill. Overrides whatever base variant is in play.
  const copiedClass =
    'border-transparent bg-[var(--success-soft)] text-[var(--success)] shadow-none hover:bg-[var(--success-soft)] hover:text-[var(--success)] hover:shadow-none';

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={onCopy}
      className={cn(copied && copiedClass, className)}
    >
      {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : icon}
      {copied ? copiedLabel : idleLabel}
    </Button>
  );
}

/** A compact icon-only copy control (dashboard rows, tight toolbars). */
export function CopyLinkIconButton({
  url,
  label,
  successMessage,
  className,
}: {
  url: string;
  label: string;
  successMessage?: string;
  className?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<number | null>(null);
  React.useEffect(() => () => void (timer.current && window.clearTimeout(timer.current)), []);

  const onCopy = React.useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
        await navigator.clipboard.writeText(url);
        setCopied(true);
        if (successMessage) notify.success(successMessage);
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 1600);
      } catch (err) {
        notify.error(err, "Couldn't copy — select and copy the link manually.");
      }
    },
    [url, successMessage],
  );

  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={label}
      title={label}
      className={cn(
        'grid h-9 w-9 shrink-0 place-items-center rounded-full border transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]',
        copied
          ? 'border-transparent bg-[var(--success-soft)] text-[var(--success)]'
          : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text-secondary)] hover:border-[color-mix(in_srgb,var(--primary)_35%,transparent)] hover:text-[var(--primary)]',
        className,
      )}
    >
      {copied ? (
        <Check className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Link2 className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * A read-only "link field" — the modern share pattern: the URL sits in an
 * input-styled box with the copy control embedded on the right. Tapping the text
 * selects the whole URL (manual-copy fallback); the icon copies + confirms
 * inline. Cleaner than a URL + a separate full-width button.
 */
export function InlineCopyField({
  url,
  label = 'Copy link',
  successMessage,
  className,
}: {
  url: string;
  label?: string;
  successMessage?: string;
  className?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<number | null>(null);
  React.useEffect(() => () => void (timer.current && window.clearTimeout(timer.current)), []);

  const onCopy = React.useCallback(async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(url);
      setCopied(true);
      if (successMessage) notify.success(successMessage);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    } catch (err) {
      notify.error(err, "Couldn't copy — tap the link to select it, then copy.");
    }
  }, [url, successMessage]);

  return (
    <div
      className={cn(
        // The focus ring is drawn INSET (inside the field's own box), never an
        // outer box-shadow spread — so it can't be clipped by, or overflow, an
        // ancestor with `overflow-hidden` (the creation card). Always contained.
        'flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] py-1.5 pl-3 pr-1.5 shadow-[var(--shadow-sm)] transition-[border-color,box-shadow] duration-150 focus-within:border-[color-mix(in_srgb,var(--primary)_55%,var(--border))] focus-within:shadow-[inset_0_0_0_2px_var(--focus-ring)]',
        className,
      )}
    >
      <span className="min-w-0 flex-1 select-all truncate font-mono text-[0.8125rem] text-[var(--text-secondary)]">
        {url}
      </span>
      <button
        type="button"
        onClick={onCopy}
        aria-label={label}
        title={label}
        className={cn(
          'grid h-8 w-8 shrink-0 place-items-center rounded-[var(--radius-sm)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]',
          copied
            ? 'text-[var(--success)]'
            : 'text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--primary)]',
        )}
      >
        {copied ? (
          <Check className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Copy className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
